import { ApolloLink, Observable, Operation } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { datadogRum } from '@datadog/browser-rum';
import { GraphQLFormattedError } from 'graphql';
import { CURRENT_AVAILABLE_GROUPS, getUserDisplayName, UserData } from 'providers/auth';
import { matchPath } from 'react-router';

const VITE_DATADOG_APPLICATION_ID = import.meta.env.VITE_DATADOG_APPLICATION_ID;
const VITE_DATADOG_CLIENT_TOKEN = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
const VITE_DATADOG_ENV = import.meta.env.VITE_DATADOG_ENV;
const VITE_DATADOG_VERSION = import.meta.env.VITE_DATADOG_VERSION;
const VITE_GRAPH_API = import.meta.env.VITE_GRAPH_API;

const sampleRate = (value: string | undefined): number => (value ? Number(value) : 100);

export const tracingOrigin = (graphApi: string | undefined): string | undefined => {
  if (!graphApi) return undefined;
  try {
    return new URL(graphApi).origin;
  } catch {
    return undefined;
  }
};

export const initRum = (): void => {
  if (!VITE_DATADOG_CLIENT_TOKEN || !VITE_DATADOG_APPLICATION_ID || !VITE_DATADOG_ENV) return;

  const graphOrigin = tracingOrigin(VITE_GRAPH_API);
  datadogRum.init({
    applicationId: VITE_DATADOG_APPLICATION_ID,
    clientToken: VITE_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'admin-ui',
    env: VITE_DATADOG_ENV,
    version: VITE_DATADOG_VERSION || undefined,
    sessionSampleRate: sampleRate(import.meta.env.VITE_DATADOG_SESSION_SAMPLE_RATE),
    sessionReplaySampleRate: sampleRate(import.meta.env.VITE_DATADOG_SESSION_REPLAY_SAMPLE_RATE),
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    // Every environment's API Gateway allows the x-datadog-* and traceparent headers in CORS.
    allowedTracingUrls: graphOrigin
      ? [{ match: graphOrigin, propagatorTypes: ['datadog', 'tracecontext'] }]
      : [],
  });
};

export const buildRumUser = ({
  user,
  groups,
  isInternal,
  userPoolId,
}: {
  user: UserData;
  groups: CURRENT_AVAILABLE_GROUPS[];
  isInternal: boolean;
  userPoolId: string | null;
}) => ({
  id: user.sub,
  email: user.email,
  name: getUserDisplayName(user) || user.email,
  userType: isInternal ? 'internal' : 'client',
  groups: [...groups],
  // Clients' company is never in the URL; each company has its own Cognito pool, so the pool
  // is the closest company identifier the browser has for them.
  ...(userPoolId ? { cognitoUserPoolId: userPoolId } : {}),
});

const NON_CAMPAIGN_SEGMENTS = new Set(['all', 'deprovision', 'create']);

export const rumRouteIds = (pathname: string): { companyId?: string; campaignId?: string } => {
  const companyCampaign = matchPath('/companies/:companyId/campaigns/:campaignId/*', pathname);
  if (companyCampaign) {
    const { companyId, campaignId } = companyCampaign.params;
    return { companyId, campaignId };
  }

  const company = matchPath('/companies/:companyId/*', pathname);
  if (company?.params.companyId && /^\d+$/.test(company.params.companyId)) {
    return { companyId: company.params.companyId };
  }

  const campaign = matchPath('/campaigns/:campaignId/*', pathname);
  const campaignId = campaign?.params.campaignId;
  if (campaignId && !NON_CAMPAIGN_SEGMENTS.has(campaignId)) {
    return { campaignId };
  }

  return {};
};

type RumDetails = Record<string, string | null | undefined>;
type RumEntityKey = 'company' | 'campaign';

const currentEntities: Partial<Record<RumEntityKey, Record<string, string>>> = {};

const setRumEntity = (key: RumEntityKey, id: string | undefined, details?: RumDetails) => {
  const previous = currentEntities[key];
  if (!id) {
    if (previous) {
      delete currentEntities[key];
      datadogRum.removeGlobalContextProperty(key);
    }
    return;
  }

  const kept = previous?.id === id ? previous : { id };
  const next: Record<string, string> = { ...kept };
  Object.entries(details ?? {}).forEach(([field, value]) => {
    if (value) next[field] = value;
  });

  if (previous && JSON.stringify(previous) === JSON.stringify(next)) return;
  currentEntities[key] = next;
  datadogRum.setGlobalContextProperty(key, next);
};

export const setRumCompany = (id: string | undefined, details?: { name?: string | null }) =>
  setRumEntity('company', id, details);

export const setRumCampaign = (
  id: string | undefined,
  details?: {
    name?: string | null;
    shortId?: string | null;
    type?: string | null;
    status?: string | null;
  },
) => setRumEntity('campaign', id, details);

const operationInfo = (operation: Operation) => {
  const definition = getMainDefinition(operation.query);
  const operationType = definition.kind === 'OperationDefinition' ? definition.operation : 'query';
  return { operationName: operation.operationName || 'anonymous', operationType };
};

const reportGraphqlError = (
  operationName: string,
  operationType: string,
  error: GraphQLFormattedError,
) => {
  const code = error.extensions?.code;
  datadogRum.addError(new Error(`GraphQL ${operationName}: ${error.message}`), {
    graphql: {
      operationName,
      operationType,
      ...(error.path ? { path: error.path.join('.') } : {}),
      ...(typeof code === 'string' ? { code } : {}),
    },
  });
};

// Mutations become named RUM actions (one per business event, e.g. "UpdateCampaignName"); queries
// are left to RUM's resource tracking. Variables are never sent because they can hold lead PII.
export const createRumGraphqlLink = (): ApolloLink =>
  new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        const { operationName, operationType } = operationInfo(operation);
        const isMutation = operationType === 'mutation';
        const startedAt = performance.now();
        const durationMs = () => Math.round(performance.now() - startedAt);

        const subscription = forward(operation).subscribe({
          next: (result) => {
            const errors = result.errors ?? [];
            errors.forEach((error) => reportGraphqlError(operationName, operationType, error));
            if (isMutation) {
              datadogRum.addAction(operationName, {
                graphql: {
                  operationName,
                  operationType,
                  success: errors.length === 0,
                  durationMs: durationMs(),
                  ...(errors.length ? { errorCount: errors.length } : {}),
                },
              });
            }
            observer.next(result);
          },
          error: (error) => {
            datadogRum.addError(
              new Error(`GraphQL ${operationName} network error: ${error?.message ?? error}`),
              { graphql: { operationName, operationType, networkError: true } },
            );
            if (isMutation) {
              datadogRum.addAction(operationName, {
                graphql: {
                  operationName,
                  operationType,
                  success: false,
                  networkError: true,
                  durationMs: durationMs(),
                },
              });
            }
            observer.error(error);
          },
          complete: () => observer.complete(),
        });

        return () => subscription.unsubscribe();
      }),
  );
